import { shippingOnlyPartnerHardwareValueInCents } from '../../lib/shippingOnlyPartnerOffer';
import { getLocalizedPricing } from '../../lib/util/money';
import { FiCurrency, ISubscriptionProduct } from '../../types';
import { GwpPartnerOffer } from '../PartnerLanding/useGwpPartnerProgram';

/**
 * A plan's list price and what the offer charges for its first term, in the shopper's currency, e.g. $189 a year and
 * $99 for the first year with $90 off. A term or currency the offer has no discount for pays the list price.
 */
export function offerPlanPricing(
  offer: GwpPartnerOffer,
  plan: Pick<ISubscriptionProduct, 'pricingInCents' | 'renewalMonths'>,
  currency: FiCurrency,
) {
  const priceInCents = getLocalizedPricing(plan.pricingInCents, currency);
  const discount = offer.firstTermDiscounts.find(({ termMonths }) => termMonths === plan.renewalMonths);
  const discountInCents = discount?.inCents[currency] ?? 0;
  return { priceInCents, firstTermInCents: Math.max(0, priceInCents - discountInCents) };
}

// the Mini's list price is only known in the currencies partner offers check out in
const MINI_VALUE_CURRENCIES = [FiCurrency.USD, FiCurrency.CAD];

/**
 * What a free-months offer gives on this plan, struck through on its card like the older partner offers: the Mini when
 * it's free, at its list price, plus the free months at the plan's monthly rate rounded to the dollar, as web values
 * them (offerFreeMonthsValueInCents). E.g. $99 + $189 / 12 x 3 = $146.
 */
export function offerGiftValueInCents(
  offer: GwpPartnerOffer,
  plan: Pick<ISubscriptionProduct, 'pricingInCents' | 'renewalMonths'>,
  currency: FiCurrency,
) {
  const { priceInCents } = offerPlanPricing(offer, plan, currency);
  const freeMonthsInCents = Math.round(((priceInCents / plan.renewalMonths) * offer.freeMonths) / 100) * 100;
  const freeMini =
    offer.device === 'mini' && offer.hardwarePriceInCents[currency] === 0 && MINI_VALUE_CURRENCIES.includes(currency);
  return (freeMini ? shippingOnlyPartnerHardwareValueInCents(currency) : 0) + freeMonthsInCents;
}
